import entries from 'lodash/entries';
import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';
import includes from 'lodash/includes';
import intersection from 'lodash/intersection';

export const sortObjectPropertyNames = (object, keys) =>
  keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: object[key],
    }),
    {}
  );

export const processInputMapping = (inputPool, mapping) =>
  flatMap(mapping, (inputName, index) => {
    if (inputName === '...') {
      return allInputs => {
        const lastIndex = mapping.length - 1;
        const left = allInputs.indexOf(mapping[index - 1]);
        const right = allInputs.indexOf(mapping[index + 1]);
        const leftInvalid = left === -1 && index !== 0;
        const rightInvalid = right === -1 && index !== lastIndex;

        if (leftInvalid || rightInvalid) return [];

        return allInputs
          .slice(
            index === 0 ? 0 : left + 1,
            index === lastIndex ? allInputs.length : right
          )
          .filter(inputName => includes(inputPool, inputName));
      };
    }

    if (includes(inputPool, inputName)) return inputName;
    else return [];
  });

const removeInputFromPool = (inputPool, inputName) => {
  if (typeof inputName === 'string') {
    const inputIndex = inputPool.indexOf(inputName);

    if (inputIndex !== -1) {
      return inputPool.splice(inputIndex, 1);
    }
  }

  return inputPool;
};

const expandInputPlaceholders = (
  inputNames,
  inputPool,
  itemsWithPlaceholders
) =>
  flatMap(itemsWithPlaceholders, item => {
    if (typeof item === 'function') {
      const spreadItems = item(inputNames);

      spreadItems.forEach(inputName =>
        removeInputFromPool(inputPool, inputName)
      );

      return spreadItems;
    }

    return item;
  });

export const sortInputs = (inputNames, order = []) => {
  if (order.length === 0) return inputNames;

  const duplicateInputs = intersection(
    ...order.filter(name => name !== '...').map(name => [name])
  );

  if (duplicateInputs.length !== 0)
    throw new Error(
      `The following duplicate inputs were found: ${flatten(
        duplicateInputs
      ).join(',')}`
    );

  // The input pool will track the inputs that have not been placed into a section.
  // It will ensure:
  // - Only inputs not already sorted into a section will be matched by '...'
  // - Inputs that are not sorted into a section can be tracked and are always rendered
  let inputPool = [...inputNames];
  const inputsAndPlaceholders = processInputMapping(inputPool, order);

  inputsAndPlaceholders.forEach(inputName =>
    removeInputFromPool(inputPool, inputName)
  );

  return expandInputPlaceholders(inputNames, inputPool, inputsAndPlaceholders);
};

export const sortInputsIntoSections = (inputNames, sections = {}) => {
  const sectionEntries = entries(sections);

  const duplicateInputs = intersection(
    ...sectionEntries.map(([, names]) => names.filter(name => name !== '...'))
  );

  if (sectionEntries.length > 1 && duplicateInputs.length !== 0)
    throw new Error(
      `The following input(s) were found in sections: ${flatten(
        duplicateInputs
      ).join(', ')}`
    );

  // The input pool will track the inputs that have not been placed into a section.
  // It will ensure:
  // - Only inputs not already sorted into a section will be matched by '...'
  // - Inputs that are not sorted into a section can be tracked and are always rendered
  let inputPool = [...inputNames];

  const sectionsWithInputsAndPlaceholders = sectionEntries.map(
    ([sectionName, sectionFilter]) => {
      const itemsWithPlaceholders = processInputMapping(
        inputPool,
        sectionFilter
      );

      itemsWithPlaceholders.forEach(inputName =>
        removeInputFromPool(inputPool, inputName)
      );

      return { sectionName, itemsWithPlaceholders };
    }
  );

  return flatMap(
    sectionsWithInputsAndPlaceholders,
    ({ itemsWithPlaceholders, sectionName }) => {
      const items = expandInputPlaceholders(
        inputNames,
        inputPool,
        itemsWithPlaceholders
      );

      if (items.length === 0) return [];

      return { sectionName, items };
    }
  ).concat(inputPool.length ? [{ items: inputPool }] : []);
};
